import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MESSAGE_PAGE_SIZE_DEFAULT,
  MESSAGE_RECEIPT_SEQS_MAX,
  type AgendaAssignee,
  type AttachmentView,
  type ConversationDetail,
  type ConversationReceiptsView,
  type ConversationSummary,
  type CreateConversationInput,
  type EditMessageInput,
  type LinkedRecordView,
  type MentionView,
  type MessageReceiptView,
  type MessageView,
  type OrgUnit,
  type Paginated,
  type SendMessageInput,
  type SetConversationArchiveInput,
  type SetConversationLinkInput,
  type SetConversationMuteInput,
} from '@asyagrup/group-hub-contracts';
import { api, apiRequest } from '@/lib/api/client';

/**
 * Data layer for Kurumsal İletişim Merkezi.
 *
 * ONE ROOT KEY, `['conversations']`. Almost every write here invalidates more
 * than the thing it wrote: sending a message moves the conversation to the top
 * of the list, changes its preview, and resets the caller's unread count, so a
 * per-endpoint key set would need every mutation to remember three targets.
 * The choke point below is bound to `onSettled`, not `onSuccess` — a request
 * that timed out may well have landed, and a screen that decided it did not is
 * wrong in the direction the user cannot recover from.
 */
export const messagingKeys = {
  all: ['conversations'] as const,
  /**
   * Every list variant at once — the prefix `list(query)` is built on.
   *
   * The live stream needs it and `all` will not do: a new message changes the
   * row's preview, its position and its unread badge, and the reader may be
   * looking at any of the filter/search combinations that produced their own
   * cache entry. Invalidating `all` on every frame would drag every loaded
   * page of the open thread along with it — the cost this layer exists to
   * remove.
   */
  listRoot: ['conversations', 'list'] as const,
  list: (query: string) => ['conversations', 'list', query] as const,
  detail: (id: string) => ['conversations', 'detail', id] as const,
  messages: (id: string) => ['conversations', 'messages', id] as const,
  pending: (id: string) => ['conversations', 'pending-attachments', id] as const,
  mentions: () => ['conversations', 'mentions'] as const,
  directory: (q: string) => ['conversations', 'directory', q] as const,
  orgUnits: () => ['conversations', 'org-units'] as const,
  /**
   * The resolved linked record — under the `all` root, so setting or clearing
   * the link refreshes the card through the same choke point every other write
   * uses. It is a separate key from `detail` because it is a separate request:
   * the conversation carries the POINTER, and turning that into a label is
   * another module's permission question.
   */
  linkedRecord: (id: string) => ['conversations', 'linked-record', id] as const,
  /**
   * Delivery and read counters, keyed by the exact seq list asked about.
   *
   * The list is part of the key because it is part of the question: a thread
   * that has grown by one message is asking about a different set of rows, and
   * reusing the previous answer would leave the newest message without a
   * receipt until the next poll.
   */
  receipts: (id: string, seqs: string) => ['conversations', 'receipts', id, seqs] as const,
};

/**
 * Polling, and why it is not a stopgap to be embarrassed about.
 *
 * The realtime stream has landed, and THIS STAYS: a screen whose correctness
 * depends on a socket is a screen that is silently wrong whenever the socket is
 * not there — behind a proxy that buffers, on a phone that slept, after a
 * deploy that cycled the API. The stream makes the numbers fast; polling is
 * what makes them true. With the stream permanently dead this module behaves
 * exactly as it shipped in v1.37.0.
 *
 * `refetchIntervalInBackground: false` on both: a hidden tab polling a thread
 * nobody is reading spends the user's battery to learn something no one will
 * look at, and react-query refetches on focus anyway.
 */
export const OPEN_CONVERSATION_POLL_MS = 15_000;
export const CONVERSATION_LIST_POLL_MS = 60_000;

/**
 * The thread's interval WHILE a stream is proven live — and the arithmetic,
 * because this is the one number the stream is allowed to change.
 *
 * A thread poll refetches EVERY loaded page, so it is the expensive one, and
 * this file already named it the strongest argument for having a stream at
 * all. Backing it off is safe only because of three guarantees that live
 * elsewhere: the client tears a silent stream down after 50s without a byte
 * (`lib/api/stream.ts`), a restored stream invalidates the whole root before
 * it is trusted again (`messaging-stream.ts`), and `live` is false the instant
 * either of those is in doubt, which snaps this straight back to 15s.
 *
 * Stated honestly: in the one case those guarantees do not cover — a socket
 * that is dead but has not yet been noticed — a new message can take up to
 * about 95 seconds instead of 15. It cannot be LOST, only late, and the badge
 * on screen says «canlı» is in question the moment the watchdog fires.
 *
 * The list stays at 60s in both modes. It is cheap, and it is also the request
 * most likely to be the one that renews an expired access token for everyone.
 */
export const OPEN_CONVERSATION_POLL_LIVE_MS = 45_000;

function qs(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  // Empty is ABSENT, not empty-string: `kind=` is not "no filter", it is asking
  // for the empty string, and `z.enum` answers that with a 400.
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  return search.toString();
}

// --- reads -------------------------------------------------------------------

export interface ConversationListParams {
  view?: 'active' | 'archived';
  kind?: string;
  unreadOnly?: 'true';
  q?: string;
}

export function useConversations(params: ConversationListParams) {
  const query = qs({ ...params, perPage: 50 });
  return useQuery({
    queryKey: messagingKeys.list(query),
    queryFn: ({ signal }) =>
      api.get<Paginated<ConversationSummary>>(`/conversations?${query}`, signal),
    refetchInterval: CONVERSATION_LIST_POLL_MS,
    refetchIntervalInBackground: false,
  });
}

/**
 * The open conversation's header, membership and pinned messages.
 *
 * Deliberately NOT on the 15-second interval the thread runs at. Participants
 * and the pinned set change on somebody pressing a button, and every one of
 * those buttons already invalidates this key — so a second timer would buy a
 * request every fifteen seconds and no fact the screen did not already have.
 */
export function useConversation(id: string | null) {
  return useQuery({
    queryKey: messagingKeys.detail(id ?? ''),
    enabled: id !== null,
    queryFn: ({ signal }) => api.get<ConversationDetail>(`/conversations/${id}`, signal),
  });
}

/**
 * The thread — CURSOR paging on `seq`, upwards.
 *
 * `before` is exclusive and the sequence is allocated under the conversation's
 * row lock, so a page can neither repeat a message nor skip one however much
 * the thread grows while it is being read. Offset paging cannot say that, which
 * is why the endpoint refuses `page`/`perPage` outright.
 *
 * A poll refetches EVERY loaded page, not just the newest. That is the price of
 * having edits, removals and stars stay honest on messages the reader has
 * already scrolled past — the alternative is a thread whose top half is a
 * snapshot from ten minutes ago. It is also the strongest argument for the M2
 * stream: with it, this interval can grow.
 */
export function useMessages(id: string | null, streamLive = false) {
  return useInfiniteQuery({
    queryKey: messagingKeys.messages(id ?? ''),
    enabled: id !== null,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) => {
      const query = qs({ limit: MESSAGE_PAGE_SIZE_DEFAULT, before: pageParam });
      return api.get<Paginated<MessageView>>(`/conversations/${id}/messages?${query}`, signal);
    },
    // `nextCursor` is the seq of the OLDEST row on the page just returned; null
    // means the thread has no more history. `?? undefined` because react-query
    // reads `undefined` — not null — as "there is no next page".
    getNextPageParam: (last) => last.meta.nextCursor ?? undefined,
    // `streamLive` DEFAULTS TO FALSE, and that direction is deliberate: a
    // caller that forgets to pass it gets the slow, always-correct interval,
    // never the fast-and-quiet one.
    refetchInterval: streamLive ? OPEN_CONVERSATION_POLL_LIVE_MS : OPEN_CONVERSATION_POLL_MS,
    refetchIntervalInBackground: false,
  });
}

/**
 * Flattens the pages into one oldest-first list with no message twice.
 *
 * Two independent guards, because they fail differently. The Map keyed by
 * message id is what survives a page boundary being refetched while a new
 * message shifts the window; sorting by `seq` — as a BigInt, since it is an
 * int8 that arrives as a string precisely so it does not round — is what puts
 * them in the order they were written rather than the order they arrived.
 */
export function mergeMessages(pages: Array<Paginated<MessageView>> | undefined): MessageView[] {
  const byId = new Map<string, MessageView>();
  for (const page of pages ?? []) {
    for (const message of page.data) byId.set(message.id, message);
  }
  return [...byId.values()].sort((a, b) => (BigInt(a.seq) < BigInt(b.seq) ? -1 : 1));
}

/** Files uploaded for a message that has not been sent yet. Survives a reload. */
export function usePendingAttachments(id: string | null) {
  return useQuery({
    queryKey: messagingKeys.pending(id ?? ''),
    enabled: id !== null,
    queryFn: ({ signal }) => api.get<AttachmentView[]>(`/conversations/${id}/attachments`, signal),
  });
}

/**
 * The record this conversation is about, as THIS reader may see it.
 *
 * A SECOND REQUEST, and that is the server's design rather than an oversight
 * here: the conversation payload carries a type and an id, and turning those
 * into a label runs the record's OWN module's visibility rule. The endpoint
 * answers with an identical `canOpen: false` payload whether the record is out
 * of scope, behind a permission, or gone — so this hook can never learn, and
 * therefore can never leak, which of the three it was.
 *
 * `enabled` is the caller's "there is a pointer at all" test: asking about a
 * conversation with no link is a request whose answer is always `null`.
 */
export function useLinkedRecord(conversationId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: messagingKeys.linkedRecord(conversationId ?? ''),
    enabled: conversationId !== null && enabled,
    queryFn: ({ signal }) =>
      api.get<LinkedRecordView | null>(`/conversations/${conversationId}/linked-record`, signal),
  });
}

/**
 * Gönderildi / iletildi / okundu, for the messages on screen.
 *
 * ONE REQUEST FOR THE WHOLE PAGE, not one per message: the endpoint takes a
 * comma-separated seq list precisely so a thread of fifty rows costs one
 * round trip. `MESSAGE_RECEIPT_SEQS_MAX` is the server's cap and asking past it
 * is a 400, so the list is trimmed to the NEWEST seqs — a receipt on a message
 * somebody scrolled past ten pages ago is not what anyone is looking at.
 *
 * `placeholderData` keeps the previous answer while a new key resolves. Without
 * it every new message blanks the labels on every older message for one round
 * trip, which reads as the ticks flickering off.
 */
export function useMessageReceipts(
  conversationId: string | null,
  seqs: readonly string[],
  streamLive = false,
) {
  const asked = seqs.slice(-MESSAGE_RECEIPT_SEQS_MAX);
  const key = asked.join(',');
  return useQuery({
    queryKey: messagingKeys.receipts(conversationId ?? '', key),
    enabled: conversationId !== null && key !== '',
    queryFn: ({ signal }) =>
      api.get<ConversationReceiptsView>(
        `/conversations/${conversationId}/receipts?seqs=${encodeURIComponent(key)}`,
        signal,
      ),
    placeholderData: (previous) => previous,
    // Receipts move without any message being sent — somebody simply opens the
    // room — so they need their own refresh rather than riding the thread's.
    refetchInterval: streamLive ? OPEN_CONVERSATION_POLL_LIVE_MS : OPEN_CONVERSATION_POLL_MS,
    refetchIntervalInBackground: false,
  });
}

/** Receipts by seq, so a row can look up its own without scanning the list. */
export function receiptsBySeq(
  view: ConversationReceiptsView | undefined,
): Map<string, MessageReceiptView> {
  return new Map((view?.receipts ?? []).map((receipt) => [receipt.seq, receipt]));
}

export function useMentions(enabled: boolean) {
  return useQuery({
    queryKey: messagingKeys.mentions(),
    enabled,
    queryFn: ({ signal }) =>
      api.get<Paginated<MentionView>>('/mentions?perPage=50&unreadOnly=true', signal),
  });
}

/**
 * Who can be put in a conversation.
 *
 * READ THIS BEFORE CHANGING THE PATH. The messaging API ships no directory
 * endpoint of its own, and `/users` needs `user.read` — a permission
 * `sistem_yoneticisi` holds alone. A picker built on it would be EMPTY for
 * exactly the people this module exists for, since `message.use` sits in
 * SELF_PERMISSIONS and therefore means everyone.
 *
 * `/agenda/assignable` is the one scoped people list the same population can
 * already read: it is gated on `agenda.use`, which sits in that same
 * SELF_PERMISSIONS list, so "may message" and "may see who to message" are the
 * same set of accounts by construction rather than by luck. Its scope filter
 * (`assignableWhere`) is the identical company bound this picker needs.
 *
 * It is still a cross-module reach and it is recorded as one: M2 should add
 * `GET /conversations/directory` under `message.use` and this hook should move
 * to it. Until then, an agenda change that narrows that list narrows this
 * picker, silently.
 */
export function useDirectory(q: string, enabled: boolean) {
  return useQuery({
    queryKey: messagingKeys.directory(q),
    enabled,
    // The roster does not move during one composing session; without this every
    // keystroke of the filter is a request.
    staleTime: 60_000,
    queryFn: ({ signal }) =>
      api.get<AgendaAssignee[]>(
        `/agenda/assignable${q ? `?q=${encodeURIComponent(q)}` : ''}`,
        signal,
      ),
  });
}

export function useOrgUnits(enabled: boolean) {
  return useQuery({
    queryKey: messagingKeys.orgUnits(),
    enabled,
    staleTime: 300_000,
    queryFn: ({ signal }) => api.get<OrgUnit[]>('/org-units?isActive=true', signal),
  });
}

// --- writes ------------------------------------------------------------------

function useInvalidate() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: messagingKeys.all });
}

export function useCreateConversation() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: CreateConversationInput) =>
      api.post<ConversationDetail>('/conversations', input),
    onSettled: invalidate,
  });
}

export function useSendMessage(conversationId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: SendMessageInput) =>
      api.post<MessageView>(`/conversations/${conversationId}/messages`, input),
    onSettled: invalidate,
  });
}

/**
 * Advances the read water mark.
 *
 * No invalidation on success, and that is not an oversight: the server takes
 * GREATEST(lastReadSeq, upToSeq), so this call cannot lower anything, and
 * refetching the whole list every time the reader's eye reaches the bottom of
 * an open thread is a request per poll for a badge that is already zero on
 * screen. The next list poll carries the new count.
 */
export function useMarkRead(conversationId: string) {
  return useMutation({
    mutationFn: (upToSeq: string) =>
      api.post<{ lastReadSeq: string; unreadCount: number }>(
        `/conversations/${conversationId}/read`,
        { upToSeq },
      ),
  });
}

/**
 * Points the conversation at a record — MEMBERSHIP is the whole gate.
 *
 * Not folded into `useSetMute`-style conversation edits, and not into the
 * PATCH the title goes through: that endpoint means "manage this channel" and
 * refuses direct threads outright, while the two people in a direct thread are
 * exactly who should be able to say what it is about.
 *
 * A record the caller cannot see is a 404 with a neutral sentence, so the
 * screen must surface the server's own message rather than inventing "yetkiniz
 * yok" — that phrasing would confirm the record exists.
 */
export function useSetLink(conversationId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: SetConversationLinkInput) =>
      api.post<LinkedRecordView>(`/conversations/${conversationId}/link`, input),
    onSettled: invalidate,
  });
}

/** Removes the pointer. Succeeds when there was none — a retry must not error. */
export function useClearLink(conversationId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: () => api.del<{ cleared: true }>(`/conversations/${conversationId}/link`),
    onSettled: invalidate,
  });
}

export function useSetArchive(conversationId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: SetConversationArchiveInput) =>
      api.post<{ archived: boolean }>(`/conversations/${conversationId}/archive`, input),
    onSettled: invalidate,
  });
}

export function useSetMute(conversationId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: SetConversationMuteInput) =>
      api.post<unknown>(`/conversations/${conversationId}/mute`, input),
    onSettled: invalidate,
  });
}

export function useEditMessage() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, ...input }: EditMessageInput & { id: string }) =>
      api.patch<MessageView>(`/messages/${id}`, input),
    onSettled: invalidate,
  });
}

/**
 * Removing a message for everyone. Soft — the body stays in the database.
 *
 * `apiRequest` rather than `api.del`, because a moderator removing somebody
 * else's message MUST send a reason and `api.del` carries no body by design.
 * Adding a body parameter to the shared client is an edit to a file this slice
 * does not own, and the transport it would go through is the same one either
 * way — `apiRequest` is what `api.del` itself calls.
 */
export function useRemoveMessage() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      apiRequest<MessageView>(`/messages/${id}`, {
        method: 'DELETE',
        body: reason ? { reason } : {},
      }),
    onSettled: invalidate,
  });
}

/** Hides a message from THIS reader only. Nobody else's screen changes. */
export function useHideMessage() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => api.del<{ hidden: true }>(`/messages/${id}/for-me`),
    onSettled: invalidate,
  });
}

export function useSetStar() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, starred }: { id: string; starred: boolean }) =>
      api.post<{ starred: boolean }>(`/messages/${id}/star`, { starred }),
    onSettled: invalidate,
  });
}

export function useSetPin() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, pinned }: { id: string; pinned: boolean }) =>
      api.post<MessageView>(`/messages/${id}/pin`, { pinned }),
    onSettled: invalidate,
  });
}

export function useUploadAttachment(conversationId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return api.postForm<AttachmentView>(`/conversations/${conversationId}/attachments`, form);
    },
    onSettled: invalidate,
  });
}

/**
 * Fetches the bytes and hands them to the browser.
 *
 * NEVER `<a href="/api/v1/…">`. The access token lives in memory only and the
 * guard accepts `Authorization: Bearer` exclusively, so a plain navigation
 * arrives with no credentials and renders 401 JSON in a broken tab. `getBlob`
 * is also the path that re-checks membership and writes the DOWNLOAD row.
 */
export async function downloadAttachment(attachmentId: string, fileName: string): Promise<void> {
  const blob = await api.getBlob(`/attachments/${attachmentId}`);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}
